import React, { useMemo } from 'react';
import moment from 'moment';
import { useWidgetContext } from '../../../context/WidgetContextProvider';
import { useScheduleContext } from '../../../context/ScheduleContext';
import { useModalContext } from '../../../context/ModalContext';
import { unixFormat } from '../../../helpers/times';
import { PAGES_VIEW_LINES, PAGES_VIEW_TILES } from '../../../constants/page-settings/schedule';
import ScheduleLineCard from '../ScheduleCards/ScheduleCardOffline/ScheduleLineCard';
import ScheduleBreakCard from '../ScheduleCards/ScheduleCardOffline/ScheduleBreakCard';
import ScheduleTileCard from '../ScheduleCards/ScheduleCardOffline/ScheduleTileCard';
import ScheduleDetailsModal from '../ScheduleDetailsModal';
import { useLocaleContext } from '../../../context/LocaleContext';

import './style.scss';

const ScheduleEpisodes = ({
    episodes,
    isActiveFavorites,
    onEpisodeClick,
    currentUser,
    checkAuth,
    onRateEpisode,
}) => {
    const {page, scheduleFilters, scheduleTimeLine, widgetSettings} = useScheduleContext();
    const {isWidget} = useWidgetContext();
    const {renderModal} = useModalContext();
    const $locale = useLocaleContext();
    const filteredIntervals = useMemo(() => {
        return scheduleTimeLine.filter((time, index, arr) => {
            return episodes.some(({timeStart}) => {
                return timeStart >= time && timeStart < arr[index + 1];
            });
        });
    }, [scheduleTimeLine, episodes]);

    const isShowWatchRecording = isWidget ? widgetSettings?.showWatchRecordButton : page.settings?.showWatchRecordButton;

    if (scheduleFilters.scheduleView === PAGES_VIEW_LINES && !isActiveFavorites) {
        return (
            episodes
                .map((episode) => (
                    <div
                        key={episode.id}
                        className={`schedule-episodes-line ${isWidget ? '-is-widget' : ''}`}
                    >
                        <div className="schedule-episodes-line-time">
                            {unixFormat(episode.timeStart, 'HH:mm')} — {unixFormat(episode.timeStop, 'HH:mm')}
                        </div>
                        {
                            !episode.delay
                                ? <ScheduleLineCard
                                    episode={episode}
                                    opened={episode.expandReports}
                                    onClick={() => renderModal(
                                        ScheduleDetailsModal,
                                        {episode, lockPageScroll: false, settings: isWidget ? widgetSettings : page.settings},
                                        null,
                                        (isWidget ? !!widgetSettings?.showEpisodePopup : true)
                                    )}
                                    currentUser={currentUser}
                                    checkAuth={checkAuth}
                                    onRateEpisode={onRateEpisode}
                                />
                                : <ScheduleBreakCard
                                    episode={episode}
                                    showTime={false}
                                />
                        }
                    </div>
                )
            )
        )
    }

    if (scheduleFilters.scheduleView === PAGES_VIEW_TILES
        && (isWidget ? +widgetSettings?.intervals > 0 : +page.settings.intervals > 0)
        && !isActiveFavorites
    ) {
        return filteredIntervals.map((timeStart, index) => (
                <div
                    key={timeStart}
                    className="schedule-episodes-tile schedule-episodes-tile--intervals"
                >
                    <div className="schedule-episodes-tile-time">
                        {moment.unix(timeStart).format('HH:mm')}
                    </div>
                    <div className="schedule-episodes-tile-list">
                        {
                            episodes
                                .filter(({timeStart: episodTimeStart}) => {
                                    if (!filteredIntervals[index + 1]) {
                                        return episodTimeStart >= timeStart;
                                    }

                                    return episodTimeStart >= timeStart && episodTimeStart < filteredIntervals[index + 1];
                                })
                                .map((episode, episodeIndex) => (
                                    <React.Fragment key={episode.id}>
                                        {
                                            !episode.delay
                                                ? episode.importantEpisode
                                                    ? <ScheduleLineCard
                                                        episode={episode}
                                                        opened={episode.expandReports}
                                                        hasIntervals
                                                        onClick={() => renderModal(
                                                            ScheduleDetailsModal,
                                                            {episode, lockPageScroll: false, settings: isWidget ? widgetSettings : page.settings},
                                                            null,
                                                            (isWidget ? !!widgetSettings?.showEpisodePopup : true)
                                                        )}
                                                        currentUser={currentUser}
                                                        checkAuth={checkAuth}
                                                        onRateEpisode={onRateEpisode}
                                                    />
                                                    : <ScheduleTileCard
                                                        episode={episode}
                                                        onClick={() => renderModal(
                                                            ScheduleDetailsModal,
                                                            {episode, lockPageScroll: false, settings: isWidget ? widgetSettings : page.settings},
                                                            null,
                                                            (isWidget ? !!widgetSettings?.showEpisodePopup : true)
                                                        )}
                                                        currentUser={currentUser}
                                                        checkAuth={checkAuth}
                                                        onRateEpisode={onRateEpisode}
                                                    />
                                                : <ScheduleBreakCard episode={episode} />
                                        }
                                    </React.Fragment>
                                ))
                        }
                    </div>
                </div>
            )
        )
    }

    if (!episodes.length) {
        return (
            <span className="schedule-episodes-label-empty">
                {$locale.get("messages.listEmpty")}
            </span>
        );
    }

    return (
        <div className="schedule-episodes-tile">
            {
                episodes.map((episode) => {
                    if (episode.delay) {
                        return (
                            <ScheduleBreakCard
                                key={episode.id}
                                episode={episode}
                            />
                        )
                    }

                    return (
                        <React.Fragment key={episode.id}>
                            {
                                episode.importantEpisode
                                    ? <ScheduleLineCard
                                        episode={episode}
                                        opened={episode.expandReports}
                                        onClick={() => renderModal(
                                            ScheduleDetailsModal,
                                            {episode, lockPageScroll: false, settings: isWidget ? widgetSettings : page.settings},
                                            null,
                                            (isWidget ? !!widgetSettings?.showEpisodePopup : true)
                                        )}
                                        currentUser={currentUser}
                                        checkAuth={checkAuth}
                                        onRateEpisode={onRateEpisode}
                                    />
                                    : <ScheduleTileCard
                                        key={episode.id}
                                        episode={episode}
                                        onClick={() => {
                                            if (onEpisodeClick) {
                                                onEpisodeClick(episode)
                                            } else {
                                                renderModal(
                                                    ScheduleDetailsModal,
                                                    {episode, lockPageScroll: false, settings: isWidget ? widgetSettings : page.settings},
                                                    null,
                                                    (isWidget ? !!widgetSettings?.showEpisodePopup : true)
                                                );
                                            }
                                        }}
                                        isShowWatchRecording={isShowWatchRecording && isActiveFavorites}
                                        currentUser={currentUser}
                                        checkAuth={checkAuth}
                                        onRateEpisode={onRateEpisode}
                                    />
                            }
                        </React.Fragment>
                    )
                })
            }
        </div>
    )
};

export default ScheduleEpisodes;
